<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">اشتراكات الطلاب</h1>
          <p class="ds-page-header__subtitle">تفعيل اشتراكات الطلاب حسب السنة الدراسية (فردي أو جماعي)</p>
        </div>
      </div>

      <!-- Filters -->
      <div class="filter">
        <div class="filter__header">
          <div class="filter__header-icon"><i class="pi pi-filter"></i></div>
          <p class="filter__title">عوامل التصفية</p>
        </div>
        <div class="ds-field-grid ds-field-grid--4">
          <div class="form-field">
            <label class="field-label">السنة الدراسية</label>
            <select class="form-select" v-model="filters.academic_year_id" @change="onYearChange">
              <option value="">اختر السنة الدراسية</option>
              <option v-for="y in years" :key="y.id" :value="y.id">{{ y.name }}</option>
            </select>
          </div>
          <div class="form-field">
            <label class="field-label">بحث بالاسم</label>
            <input type="text" class="form-control" v-model="filters.search" @keyup.enter="reload" placeholder="اسم الطالب" />
          </div>
          <div class="form-field">
            <label class="field-label">رقم الطالب</label>
            <input type="number" class="form-control" v-model="filters.student_id" @keyup.enter="reload" placeholder="ID" dir="ltr" />
          </div>
          <div class="form-field">
            <label class="field-label">حالة الاشتراك</label>
            <select class="form-select" v-model="filters.status" @change="reload">
              <option value="all">الكل</option>
              <option value="subscribed">مشترك</option>
              <option value="unsubscribed">غير مشترك</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Bulk action bar -->
      <div v-if="filters.academic_year_id" class="d-flex align-items-center gap-2 my-3 flex-wrap">
        <button class="ds-btn ds-btn--primary" :disabled="!selectedIds.length || activating" @click="activateSelected">
          <i class="pi pi-check-circle"></i>
          تفعيل الاشتراك<span v-if="selectedIds.length"> ({{ selectedIds.length }})</span>
        </button>
        <button class="ds-btn ds-btn--secondary" @click="reload" v-tooltip.top="'تحديث'">
          <i class="pi pi-refresh"></i>
        </button>
        <span v-if="yearMeta && !yearMeta.is_subscription_enabled" class="status-badge status-badge--warn">
          تنبيه: حماية المحتوى غير مفعّلة لهذه السنة (المحتوى متاح للجميع)
        </span>
      </div>

      <Loading v-if="loading" />

      <template v-else-if="filters.academic_year_id">
        <div v-if="!students.data || !students.data.length" class="empty-state empty-state--card">
          <div class="empty-state__icon"><i class="pi pi-users"></i></div>
          <h3 class="empty-state__title">لا يوجد طلاب</h3>
          <p class="empty-state__desc">لم يتم العثور على طلاب مطابقين لعوامل التصفية.</p>
        </div>

        <template v-else>
          <div class="ds-table-wrap table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th style="width:44px" class="text-center">
                    <input type="checkbox" :checked="allOnPageSelected" @change="toggleSelectAll" />
                  </th>
                  <th>#</th>
                  <th>اسم الطالب</th>
                  <th>رقم الهاتف</th>
                  <th class="text-center">حالة الاشتراك</th>
                  <th class="text-center">إجراء</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(s, i) in students.data" :key="s.id">
                  <td class="text-center">
                    <input type="checkbox" :value="s.id" :checked="selected.has(s.id)" @change="toggleRow(s.id)" />
                  </td>
                  <td>{{ rowNumber(i) }}</td>
                  <td>{{ s.name }}</td>
                  <td dir="ltr" class="text-start">{{ s.phone || '—' }}</td>
                  <td class="text-center">
                    <span class="status-badge" :class="s.subscribed ? 'status-badge--ok' : 'status-badge--muted'">
                      {{ s.subscribed ? 'مشترك' : 'غير مشترك' }}
                    </span>
                  </td>
                  <td class="text-center">
                    <button v-if="s.subscribed" class="ds-btn ds-btn--secondary ds-btn--sm" disabled>مشترك</button>
                    <button v-else class="ds-btn ds-btn--primary ds-btn--sm" :disabled="activating" @click="activateOne(s)">
                      تفعيل الاشتراك
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="d-flex justify-content-center mt-3">
            <pagination :data="students" @pagination-change-page="fetchStudents" class="pagination mb-0" />
          </div>
        </template>
      </template>

      <div v-else class="empty-state empty-state--card">
        <div class="empty-state__icon"><i class="pi pi-calendar"></i></div>
        <h3 class="empty-state__title">اختر السنة الدراسية</h3>
        <p class="empty-state__desc">اختر سنة دراسية لعرض الطلاب المرتبطين بها وإدارة اشتراكاتهم.</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import pagination from 'laravel-vue-pagination';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: 'StudentSubscriptions',
  components: { pagination, Breadcrumb, Loading },
  data() {
    return {
      filters: { academic_year_id: '', search: '', student_id: '', status: 'all' },
      students: { data: [] },   // Laravel paginator object
      yearMeta: null,
      selected: new Set(),
      loading: false,
      activating: false,
      currentPage: 1,
    };
  },
  computed: {
    gradeStore() { return useGradeStore(); },
    years() { return this.gradeStore.allGrades || []; },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: 'اشتراكات الطلاب', path: '/student-subscriptions' },
      ];
    },
    selectedIds() { return Array.from(this.selected); },
    allOnPageSelected() {
      const rows = this.students.data || [];
      return rows.length > 0 && rows.every((s) => this.selected.has(s.id));
    },
  },
  methods: {
    apiError(e) { return e?.response?.data?.message || 'حدث خطأ، برجاء المحاولة مرة أخرى'; },
    rowNumber(i) {
      const from = this.students.from || ((this.currentPage - 1) * (this.students.per_page || 20) + 1);
      return from + i;
    },
    onYearChange() { this.selected.clear(); this.fetchStudents(1); },
    reload() { this.fetchStudents(1); },
    async fetchStudents(page = 1) {
      if (!this.filters.academic_year_id) { this.students = { data: [] }; return; }
      this.loading = true;
      this.currentPage = page;
      try {
        const { data } = await axios.get('/admin/student-subscriptions', {
          params: {
            academic_year_id: this.filters.academic_year_id,
            search: this.filters.search || undefined,
            student_id: this.filters.student_id || undefined,
            status: this.filters.status,
            page,
          },
        });
        this.students = data.data;          // paginator
        this.yearMeta = data.academic_year;
        // Drop selections that are no longer on the current page's dataset.
        const ids = new Set((this.students.data || []).map((s) => s.id));
        this.selected.forEach((id) => { if (!ids.has(id)) this.selected.delete(id); });
      } catch (e) {
        Swal.fire({ icon: 'error', title: this.apiError(e), timer: 2500, showConfirmButton: false });
      } finally {
        this.loading = false;
      }
    },
    toggleRow(id) {
      if (this.selected.has(id)) this.selected.delete(id); else this.selected.add(id);
      this.selected = new Set(this.selected); // trigger reactivity
    },
    toggleSelectAll() {
      const rows = this.students.data || [];
      if (this.allOnPageSelected) {
        rows.forEach((s) => this.selected.delete(s.id));
      } else {
        rows.forEach((s) => this.selected.add(s.id));
      }
      this.selected = new Set(this.selected);
    },
    async activateOne(student) {
      await this.postActivate([student.id]);
    },
    async activateSelected() {
      if (!this.selectedIds.length) return;
      const res = await Swal.fire({
        icon: 'question',
        title: 'تفعيل الاشتراك',
        text: `سيتم تفعيل الاشتراك لـ ${this.selectedIds.length} طالب في هذه السنة الدراسية.`,
        showCancelButton: true,
        confirmButtonText: 'تفعيل',
        cancelButtonText: 'إلغاء',
      });
      if (!res.isConfirmed) return;
      await this.postActivate(this.selectedIds);
    },
    async postActivate(ids) {
      this.activating = true;
      try {
        const { data } = await axios.post('/admin/student-subscriptions/activate', {
          academic_year_id: this.filters.academic_year_id,
          student_ids: ids,
        });
        Swal.fire({ icon: 'success', title: data.message || `تم تفعيل الاشتراك لـ ${data.count} طالب بنجاح`, timer: 1800, showConfirmButton: false });
        this.selected.clear();
        this.selected = new Set();
        await this.fetchStudents(this.currentPage);
      } catch (e) {
        Swal.fire({ icon: 'error', title: this.apiError(e), timer: 2500, showConfirmButton: false });
      } finally {
        this.activating = false;
      }
    },
  },
  async mounted() {
    if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
  },
};
</script>
